/**
 * Componente ExportButton
 * 
 * Botão de exportação com dropdown para escolher formato (PDF ou CSV).
 * 
 * @module components/Dashboard/DashboardExport/ExportButton
 */

import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FaFilePdf, FaFileExcel, FaDownload, FaChevronDown } from 'react-icons/fa';
import { generateDashboardReport } from '../../../utils/dashboard/exportUtils';

/**
 * Componente de botão de exportação
 * 
 * @param {object} props - Propriedades do componente
 * @param {object} props.dashboardData - Dados do dashboard para exportação
 * @param {object} [props.options] - Opções de exportação (period, organizationName, etc.)
 * @param {function} [props.onExportStart] - Callback quando exportação inicia
 * @param {function} [props.onExportComplete] - Callback quando exportação completa
 * @param {function} [props.onExportError] - Callback quando exportação falha
 * @param {string} [props.className] - Classes CSS adicionais
 * 
 * @returns {JSX.Element} Componente de botão de exportação
 */
const ExportButton = ({
    dashboardData,
    options = {},
    onExportStart = null,
    onExportComplete = null,
    onExportError = null,
    className = '',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    /**
     * Handler para exportação
     */
    const handleExport = useCallback(async (format) => {
        setIsExporting(true);
        setIsOpen(false);

        try {
            if (onExportStart) {
                onExportStart(format);
            }

            await generateDashboardReport(dashboardData, format, options);

            if (onExportComplete) {
                onExportComplete(format);
            }
        } catch (error) {
            console.error('Erro ao exportar dashboard:', error);
            if (onExportError) {
                onExportError(error, format);
            }
        } finally {
            setIsExporting(false);
        }
    }, [dashboardData, options, onExportStart, onExportComplete, onExportError]);

    return (
        <div className={`relative ${className}`}>
            {/* Botão principal */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                disabled={isExporting}
                className="
                    group
                    flex items-center gap-1.5 sm:gap-2 
                    px-3 sm:px-4 py-2 sm:py-2.5 
                    text-xs sm:text-sm font-semibold
                    text-gray-700 
                    bg-gradient-to-r from-white to-gray-50
                    border border-gray-200 
                    rounded-xl 
                    shadow-sm
                    hover:from-gray-50 hover:to-gray-100
                    hover:border-emerald-300
                    hover:shadow-md
                    hover:scale-105
                    active:scale-100
                    active:from-gray-100 active:to-gray-200
                    transition-all duration-300 ease-in-out
                    focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                "
                aria-label="Exportar dashboard"
                aria-expanded={isOpen}
                aria-haspopup="true"
            >
                <FaDownload 
                    className={`text-gray-500 group-hover:text-emerald-600 transition-colors duration-200 ${
                        isExporting ? 'animate-pulse' : ''
                    }`}
                    size={16}
                    aria-hidden="true"
                />
                <span className="group-hover:text-emerald-700 transition-colors duration-200 hidden sm:inline">
                    {isExporting ? 'Exportando...' : 'Exportar'}
                </span>
                <FaChevronDown 
                    className={`text-gray-400 transition-transform duration-200 ${
                        isOpen ? 'transform rotate-180' : ''
                    }`}
                    size={12}
                    aria-hidden="true"
                />
            </button>

            {/* Dropdown de opções */}
            {isOpen && (
                <>
                    {/* Overlay para fechar ao clicar fora */}
                    <div
                        className="fixed inset-0 z-10"
                        onClick={() => setIsOpen(false)}
                        aria-hidden="true"
                    />
                    
                    {/* Menu dropdown */}
                    <div
                        className="
                            absolute right-0 mt-2 w-48
                            bg-white
                            border border-gray-200
                            rounded-lg
                            shadow-xl
                            z-20
                        "
                        role="menu"
                        aria-label="Opções de exportação"
                    >
                        {/* Opção PDF */}
                        <button
                            type="button"
                            onClick={() => handleExport('pdf')}
                            disabled={isExporting}
                            className="
                                w-full
                                flex items-center gap-3
                                px-4 py-3
                                text-sm text-gray-700
                                hover:bg-gray-50
                                focus:outline-none focus:bg-gray-50
                                transition-colors duration-150
                                disabled:opacity-50 disabled:cursor-not-allowed
                                first:rounded-t-lg
                            "
                            role="menuitem"
                        >
                            <FaFilePdf className="text-red-600" size={18} aria-hidden="true" />
                            <span className="flex-1 text-left">Exportar PDF</span>
                        </button>

                        {/* Opção CSV */}
                        <button
                            type="button"
                            onClick={() => handleExport('csv')}
                            disabled={isExporting}
                            className="
                                w-full
                                flex items-center gap-3
                                px-4 py-3
                                text-sm text-gray-700
                                hover:bg-gray-50
                                focus:outline-none focus:bg-gray-50
                                transition-colors duration-150
                                disabled:opacity-50 disabled:cursor-not-allowed
                                last:rounded-b-lg
                            "
                            role="menuitem"
                        >
                            <FaFileExcel className="text-green-600" size={18} aria-hidden="true" />
                            <span className="flex-1 text-left">Exportar CSV</span>
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

ExportButton.propTypes = {
    /** Dados do dashboard para exportação */
    dashboardData: PropTypes.shape({
        summary: PropTypes.object,
        taskDistribution: PropTypes.array,
        workload: PropTypes.array,
        equipmentStatus: PropTypes.array,
        pontoDesvios: PropTypes.array,
        estoqueAlertas: PropTypes.array,
    }).isRequired,
    
    /** Opções de exportação */
    options: PropTypes.shape({
        period: PropTypes.string,
        visibleKPIs: PropTypes.arrayOf(PropTypes.string),
        organizationName: PropTypes.string,
    }),
    
    /** Callback quando exportação inicia */
    onExportStart: PropTypes.func,
    
    /** Callback quando exportação completa */
    onExportComplete: PropTypes.func,
    
    /** Callback quando exportação falha */
    onExportError: PropTypes.func,
    
    /** Classes CSS adicionais */
    className: PropTypes.string,
};

ExportButton.defaultProps = {
    options: {},
    onExportStart: null,
    onExportComplete: null,
    onExportError: null,
    className: '',
};

export default ExportButton;

